import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { endLocalSession } from '../../../../../../../../crud/management_api/local_sessions';
import { flushLastSessionByLocalId } from '../../../../../../../../crud/payments_api/cache';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';
import {PendingTable} from "../../../../../Panel/components/Pending/PendingTable"
import { isSuperAdmin } from 'utils/functions';
import './EndSession.scss'
import StateSummary from './components/StateSummary/StateSummary';
import { Pendings } from './components/Pendings';

interface ModalProps {
  session: any;                   // Objeto con la información de la sesión
  show: boolean;                  // Controla visibilidad del modal
  handleClose: () => void;        // Función para cerrar el modal
}


const EndSession: React.FC<ModalProps> = ({
  session,
  show,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false);
  // Estado para el modal de error
  const [showPendingModal, setShowPendingModal] = useState(false);
  // Opción para saltar la sesión (sólo visible para super admin)
  const [skipSession, setSkipSession] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  // New state for handling pending items
  const [hasPendings, setHasPendings] = useState<boolean | null>(null);
  const [pendingsLoaded, setPendingsLoaded] = useState(false);
  // State for API error messages
  const [apiError, setApiError] = useState<string | null>(null);
  const localSessionId: number | null =
    session?.local_session_id ?? session?.localSessionId ?? null;

  // Reset acknowledgment when toggling skip option
  useEffect(() => {
    if (!skipSession) setAcknowledged(false)
  }, [skipSession])

  // Reset pending state when modal opens/closes
  useEffect(() => {
    if (show && localSessionId) {
      setHasPendings(null);
      setPendingsLoaded(false);
      setApiError(null); // Reset error when modal opens
    }
  }, [show, localSessionId]);

  // Callback to handle pending items loaded
  const handlePendingsLoaded = (pendingsExist: boolean) => {
    setHasPendings(pendingsExist);
    setPendingsLoaded(true);
  };

  // Callback to refresh pendings after resolution
  const handlePendingsResolved = () => {
    setHasPendings(null);
    setPendingsLoaded(false);
  };
  

  const handleEndSession = () => {
    if (!localSessionId) {
      toast.error('No hay una sesión de local activa para finalizar.');
      return;
    }

    if (hasPendings && !skipSession) {
      toast.error('Debes resolver los elementos pendientes antes de finalizar la sesión.');
      setShowPendingModal(true);
      return;
    }

    setLoading(true);
    setApiError(null); // Reset error before making request
    
    // Get local_id for cache flush
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user ? Number(user.local_id) : 0;
    
    endLocalSession(localSessionId, skipSession)
      .then(async (response) => {
        // Check if response is successful (status 200-299)
        if (response?.status >= 200 && response?.status < 300) {
          // Flush cache immediately after successful session end
          try {
            if (localId) {
              await flushLastSessionByLocalId(localId);
            }
          } catch (cacheError) {
            console.error('Error flushing cache (non-critical):', cacheError);
            // Don't fail the whole operation if cache flush fails
          }
          
          toast.success('Sesión finalizada correctamente');
          handleClose();
          setLoading(false);
          setTimeout(() => window.location.reload(), 800);
        } else {
          // Non-200 response
          setLoading(false);
          const errorMessage = response?.data?.message || 'Error al finalizar la sesión. Por favor, intenta nuevamente.';
          setApiError(errorMessage);
          toast.error(errorMessage);
        }
      })
      .catch((error) => {
        // Network error or request failed
        setLoading(false);
        const errorMessage = error?.response?.data?.message || 
                            error?.message || 
                            'Error al finalizar la sesión. Por favor, verifica que no haya elementos pendientes e intenta nuevamente.';
        setApiError(errorMessage);
        toast.error(errorMessage);
        // If there are pending items in the error, show the pending modal
        if (error?.response?.data?.pending_items || error?.response?.status === 400) {
          setShowPendingModal(true);
        }
      });
  };

  
  return (
    <>
      {/* Modal principal para finalizar sesión */}
      <Modal show={show} onHide={handleClose} scrollable dialogClassName="end-session-modal">
        <Modal.Header>
          <div className='form-modal-title-container'>
            <Modal.Title className='form-modal-title'>Finalizar sesión</Modal.Title>
          </div>
          <Button variant="none" onClick={handleClose}>
            <X size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          {/* Show API error if present */}
          {apiError && (
            <div className="api-error-banner">
              <div className="api-error-icon">⚠️</div>
              <div className="api-error-content">
                <strong>Error al finalizar la sesión</strong>
                <p>{apiError}</p>
              </div>
            </div>
          )}

          {localSessionId && (
            <Pendings 
              localSessionId={localSessionId} 
              onPendingsLoaded={handlePendingsLoaded}
              excludeCajas={true}
            />
          )}

          {/* Only show the confirmation text and skip options if no pendings or if skipping */}
          {pendingsLoaded && (!hasPendings || skipSession) && (
            <>
              {!hasPendings && (
                <div className="session-confirmation">
                  <p>¿Estás seguro de que deseas finalizar la sesión actual?</p>
                </div>
              )}
              
              {isSuperAdmin() && (
                <div className="skip-session-block">
                  <div className="info-banner">
                    <strong>Opción avanzada:</strong> Saltarse la sesión
                    <div className="info-text">
                      Si marcas esta opción, todo lo realizado en esta sesión no será considerado en el registro.
                    </div>
                  </div>
                  {/*
                  <label className="skip-switch" aria-label="Omitir sesión">
                    <input
                      type="checkbox"
                      checked={skipSession}
                      onChange={(e) => setSkipSession(e.target.checked)}
                      disabled={loading}
                    />
                    <span className="switch-slider" aria-hidden="true" />
                    <span className="switch-label">Omitir sesión</span>
                  </label>*/}
                  {skipSession && (
                    <>
                      <StateSummary show={true} localSessionId={localSessionId} onLoadingChange={setSummaryLoading} />
                      {!summaryLoading && (
                        <label className="skip-checkbox" style={{ marginTop: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={(e) => setAcknowledged(e.target.checked)}
                            disabled={loading}
                          />
                          <span>
                            Certifico que he leído y entiendo que estos datos serán eliminados y que no podré recuperarlos
                          </span>
                        </label>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <Button variant='none' className="form-button-cancel" onClick={handleClose}>
              Cancelar
            </Button>
            {/* Only show finish button if no pendings exist or if we're skipping session */}
            {pendingsLoaded && (!hasPendings || skipSession) && (
              <Button
                variant='none'
                className="form-button-create"
                onClick={handleEndSession}
                disabled={loading || (isSuperAdmin() && skipSession && (summaryLoading || !acknowledged))}
              >
                {loading ? 'Cargando...' : 'Finalizar'}
              </Button>
            )}
            {/* Show buttons when there are pendings */}
            {pendingsLoaded && hasPendings && !skipSession && (
              <>
                <Button
                  variant='none'
                  className="form-button-create"
                  onClick={() => {
                    handleClose(); // Cierra el modal principal de finalizar sesión
                    setShowPendingModal(true); // Abre el modal de pendientes
                  }}
                  disabled={loading}
                >
                  Ir a resolver
                </Button>
                <Button
                  variant='none'
                  className="form-button-secondary"
                  onClick={handlePendingsResolved}
                  disabled={loading}
                >
                  Verificar nuevamente
                </Button>
              </>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal de error */}
      <Modal
        show={showPendingModal}
        onHide={() => setShowPendingModal(false)}
        centered
        size="lg"
      >
        <Modal.Header>
          <div className='form-modal-title-container'>
            <Modal.Title className='form-modal-title'>Pendientes</Modal.Title>
          </div>
          <Button variant="none" onClick={() => setShowPendingModal(false)}>
            <X size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          {/* Mostrar PendingTable siempre en el modal de pendientes para depuración/flujo */}
          <div style={{ display: 'none' }} data-debug="pending-local-id">{localSessionId}</div>
          <PendingTable
            localSessionId={localSessionId ?? undefined}
            setActioncaja={() => setShowPendingModal(false)}
            onNavigate={() => setShowPendingModal(false)}
            autoExpandFirst={true}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <Button variant='none' className="form-button-cancel" onClick={() => setShowPendingModal(false)}>
              Cerrar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal para cerrar caja individual */}
      
    </>
  );
};

export default EndSession;
